import type { StateCreator } from 'zustand';
import { showErrorToastAndLog } from 'services/toaster';
import { issueClient } from 'shared/domain/issue/issueClient';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import type { IssuesQuery } from 'shared/domain/issue/issueQuery';
import { subscribeToEntityChanges } from 'store/entityCollection/subscribeToEntityChanges';
import { createLatestRead } from 'store/latestRead';

/** Issues on the documentation shown as a level's map, filtered like the grid. */
export type IssuesOnMapSlice = {
  issuesOnMap: IssueModel[];
  /** Reads the issues for the query; the query already shown (or being read) is not read again. */
  loadIssuesOnMap: (query: IssuesQuery) => Promise<void>;
  /** Reads the shown issues again after issue changes while the documentation is open; returns the close (resets). */
  followIssuesOnMap: () => () => void;
  resetIssuesOnMap: () => void;
};

export const createIssuesOnMapSlice: StateCreator<IssuesOnMapSlice> = (set) => {
  const latestRead = createLatestRead();
  let shownQuery: IssuesQuery | undefined;
  let shownQueryKey: string | undefined;

  const readIssuesOnMap = (query: IssuesQuery): Promise<void> => {
    shownQuery = query;
    shownQueryKey = JSON.stringify(query);
    return latestRead.run(() => issueClient.getFiltered(query), {
      onResult: ({ items }) => set({ issuesOnMap: items }),
      onError: (error) => {
        shownQueryKey = undefined;
        showErrorToastAndLog('Error while getting issues on map', error);
      },
    });
  };

  const resetIssuesOnMap = (): void => {
    latestRead.cancel();
    shownQuery = undefined;
    shownQueryKey = undefined;
    set({ issuesOnMap: [] });
  };

  return {
    issuesOnMap: [],
    loadIssuesOnMap: (query) =>
      JSON.stringify(query) === shownQueryKey ? Promise.resolve() : readIssuesOnMap(query),
    followIssuesOnMap: () => {
      const stopFollowingChanges = subscribeToEntityChanges(issueClient, () => {
        if (shownQuery) {
          void readIssuesOnMap(shownQuery);
        }
      });
      return (): void => {
        stopFollowingChanges();
        resetIssuesOnMap();
      };
    },
    resetIssuesOnMap,
  };
};
